import { css } from '@emotion/react';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { animated, useTransition } from '@react-spring/web';
import { useInView } from 'react-intersection-observer';
import moment from 'moment';
import t from 'react-translate';
import CollapsiblePanel from 'athena/components/collapsible-panel';
import MentoringProgramContext from 'athena/components/mentoring-program/context';
import GoalItem from 'athena/components/goal-item';
import { DEFAULT_GOAL_ITEM } from 'athena/components/goal-item/constants';
import { PanelStateKey, tooltipZIndex } from 'athena/components/constants';
import useConnection from 'athena/hooks/use-connection';
import { useMentoringProgramGoals } from 'athena/hooks/use-mentoring-program-goals';
import { MentoringProgramEnrollment } from 'redux/schemas/models/mentoring-program-enrollments';
import {
  getMentoringProgramGoalItems,
  getMentoringProgramGoals,
} from 'redux/selectors/mentorship-program-goal-items';
import { selectGuidesForMyGoalsByRole } from 'redux/selectors/mentorship-program-guides';
import {
  headerLineHeight,
  textLargeBodyFontSize,
} from 'styles/global_defaults/fonts';
import { gray2, gray4, gray7 } from 'styles/global_defaults/colors';
import { halfSpacing, quarterSpacing, standardSpacing } from 'styles/global_defaults/scaffolding';
import NvIcon from 'shared/components/nv-icon';
import ClickableContainer from 'components/clickable-container';
import NvTooltip from 'shared/components/nv-tooltip';
import { novoEdBlack } from 'athena/styles/colors';
import { config } from '@config/pendo.config.json';
import { MentoringProgramMatchingType } from 'redux/schemas/models/mentoring-programs';
import GuidanceSection from '../guidance-section';

const emptyStateStyles = css`
  font-size: ${textLargeBodyFontSize}px;
  line-height: ${headerLineHeight}px;
  color: ${gray2};
`;

const subSectionHeaderStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: ${standardSpacing}px;
  padding-bottom: ${standardSpacing}px;
  color: ${novoEdBlack};

  .add-goal-btn {
    color: ${gray2};
    padding: ${quarterSpacing}px;

    &[aria-disabled='true'] {
      color: ${gray4};
    }

    &:hover:not([aria-disabled='true']) {
      background: ${gray7};
      border-radius: 50%;
    }
  }
`;


const GoalsList = () => {
  const { ref: myGoalsRef, inView: isMyGoalsEndReached } = useInView();
  const { ref: connectionGoalsRef, inView: isConnectionGoalsEndReached } = useInView();

  const { mentoringProgram } = useContext(MentoringProgramContext);
  const {
    id: mentorshipProgramId,
    mentorshipProgramEnrollment: currentEnrollmentId,
    matchAnnouncementDate,
    guidanceEnabled,
    matchingType,
  } = mentoringProgram || {};

  const { connection } = useConnection(currentEnrollmentId);
  const { assignedEnrollment } = connection || {};
  const { id: connectionEnrollmentId, user } = assignedEnrollment || {};
  const { firstName: connectionFirstName } = user || {};
  const activeConnectionId = connection?.connectionId;

  const mentoringProgramEnrollment: MentoringProgramEnrollment = useSelector(state => (
    state.models.mentoringProgramEnrollments[currentEnrollmentId]
  ));
  const enrollments = (mentoringProgramEnrollment && assignedEnrollment)
    ? {
      [currentEnrollmentId]: mentoringProgramEnrollment,
      [assignedEnrollment.id]: assignedEnrollment as MentoringProgramEnrollment,
    }
    : undefined;
  const { role } = mentoringProgramEnrollment || {};
  const guides = useSelector(selectGuidesForMyGoalsByRole(role.name));

  const [myGoalsEditingId, setMyGoalsEditingId] = useState<number>();
  const [connectionGoalsEditingId, setConnectionGoalsEditingId] = useState<number>();
  const [showMyGoalInput, setShowMyGoalInput] = useState(false);
  const [showConnectionGoalInput, setShowConnectionGoalInput] = useState(false);

  const { loadMore: loadMoreMyGoals } = useMentoringProgramGoals({
    mentorshipProgramId,
    enrollmentId: currentEnrollmentId,
    connectionId: activeConnectionId,
  });
  const { totalCount: myTotalCount = 0 } = useSelector(getMentoringProgramGoalItems(currentEnrollmentId)) || {};
  const myGoalItems = useSelector(getMentoringProgramGoals(currentEnrollmentId)) || [];

  const { loadMore: loadMoreConnectionGoals } = useMentoringProgramGoals({
    mentorshipProgramId,
    enrollmentId: connectionEnrollmentId,
    connectionId: activeConnectionId,
  });
  const { totalCount: connectionTotalCount = 0 } = useSelector(getMentoringProgramGoalItems(connectionEnrollmentId)) || {};
  const connectionGoalItems = useSelector(getMentoringProgramGoals(connectionEnrollmentId)) || [];

  const animationConfig = {
    keys: (item) => item.id,
    from: { opacity: 0, height: 0 },
    enter: { opacity: 1, height: 'auto' },
    leave: { opacity: 0, height: 0 },
    config: { duration: 300 },
  };

  const myGoalTransitions = useTransition(
    [showMyGoalInput ? { ...DEFAULT_GOAL_ITEM } : null, ...myGoalItems].filter(Boolean),
    animationConfig,
  );

  const connectionGoalTransitions = useTransition(
    [showConnectionGoalInput ? { ...DEFAULT_GOAL_ITEM } : null, ...connectionGoalItems].filter(Boolean),
    animationConfig,
  );

  useEffect(() => {
    if (isMyGoalsEndReached) loadMoreMyGoals();
  }, [isMyGoalsEndReached, loadMoreMyGoals]);

  useEffect(() => {
    if (isConnectionGoalsEndReached) loadMoreConnectionGoals();
  }, [isConnectionGoalsEndReached, loadMoreConnectionGoals]);

  const announcementDateHasPassed = matchAnnouncementDate && moment().isAfter(moment(matchAnnouncementDate));
  const isParticipantDrivenMatching = matchingType === MentoringProgramMatchingType.PARTICIPANT_DRIVEN;
  const showConnectionSection = connection && (announcementDateHasPassed || isParticipantDrivenMatching);

  const translationBase = t.MENTORING_PROGRAMS.PARTICIPANT_HOME.GOALS;
  const addGoalLabel = translationBase.ARIA_LABEL.ADD_GOAL();
  const panelTitle = translationBase.TITLE();

  const handleSetMyGoalsEditingId = useCallback((id: number | null) => {
    if (id === null && myGoalsEditingId === DEFAULT_GOAL_ITEM.id) {
      setShowMyGoalInput(false);
    }
    setMyGoalsEditingId(id);
  }, [myGoalsEditingId]);

  const handleSetConnectionGoalsEditingId = useCallback((id: number | null) => {
    if (id === null && connectionGoalsEditingId === DEFAULT_GOAL_ITEM.id) {
      setShowConnectionGoalInput(false);
    }
    setConnectionGoalsEditingId(id);
  }, [connectionGoalsEditingId]);

  const onAddMyGoal = () => {
    setShowMyGoalInput(true);
    setMyGoalsEditingId(DEFAULT_GOAL_ITEM.id);
  };

  const onAddConnectionGoal = () => {
    setShowConnectionGoalInput(true);
    setConnectionGoalsEditingId(DEFAULT_GOAL_ITEM.id);
  };

  return (
    <CollapsiblePanel
      title={panelTitle}
      panelState={{
        persistState: true,
        localStorageKey: PanelStateKey.GOAL_ITEMS_STATE,
      }}
      ariaLabel={panelTitle}
      dataQa={config.pendo.athena.mentorshipProgram.participantHome.goals.list.goalsPanel}
    >
      <>
        <div>
          <div css={subSectionHeaderStyles}>
            <span className='text-cta' role='heading' aria-level={4}>
              {translationBase.MY_GOALS_TITLE(myTotalCount)}
            </span>
            <NvTooltip text={addGoalLabel} placement='top' zIndex={tooltipZIndex} enabled={!showMyGoalInput}>
              <ClickableContainer
                onClick={onAddMyGoal}
                className='add-goal-btn'
                aria-label={addGoalLabel}
                aria-expanded={showMyGoalInput}
                disabled={showMyGoalInput}
                data-qa={config.pendo.athena.mentorshipProgram.participantHome.goals.list.myGoalsAddGoal}
              >
                <NvIcon icon='add' size='smallest' />
              </ClickableContainer>
            </NvTooltip>
          </div>
          <div>
            {myGoalTransitions((style, item) => (
              <animated.div style={style}>
                <GoalItem
                  item={item}
                  isReadOnly={false}
                  isEditing={myGoalsEditingId === item.id}
                  setEditingId={handleSetMyGoalsEditingId}
                  enrollmentId={currentEnrollmentId}
                  connectionId={activeConnectionId}
                  enrollments={enrollments}
                />
              </animated.div>
            ))}
            <div ref={myGoalsRef} />
          </div>
        </div>

        {showConnectionSection && (
          <div>
            <div css={subSectionHeaderStyles}>
              <span className='text-cta' role='heading' aria-level={4}>
                {translationBase.CONNECTION_GOALS_TITLE(connectionFirstName, connectionTotalCount)}
              </span>
              <NvTooltip text={addGoalLabel} placement='top' zIndex={tooltipZIndex} enabled={!showConnectionGoalInput}>
                <ClickableContainer
                  onClick={onAddConnectionGoal}
                  className='add-goal-btn'
                  aria-label={addGoalLabel}
                  aria-expanded={showConnectionGoalInput}
                  disabled={showConnectionGoalInput}
                  data-qa={config.pendo.athena.mentorshipProgram.participantHome.goals.list.connectionGoalsAddGoal}
                >
                  <NvIcon icon='add' size='smallest' />
                </ClickableContainer>
              </NvTooltip>
            </div>
            <div>
              {connectionGoalTransitions((style, item) => (
                <animated.div style={style}>
                  <GoalItem
                    item={item}
                    isReadOnly={false}
                    isEditing={connectionGoalsEditingId === item.id}
                    setEditingId={handleSetConnectionGoalsEditingId}
                    enrollmentId={connectionEnrollmentId}
                    connectionId={activeConnectionId}
                    enrollments={enrollments}
                  />
                </animated.div>
              ))}
              {!connectionGoalItems.length && !showConnectionGoalInput && (
                <div css={emptyStateStyles} className='my-4 text-align-center semi-bold'>
                  {translationBase.EMPTY_LIST(connectionFirstName)}
                </div>
              )}
              <div ref={connectionGoalsRef} />
            </div>
          </div>
        )}

        {guidanceEnabled && <GuidanceSection guides={guides} className='mt-4' />}
      </>
    </CollapsiblePanel>
  );
};

export default GoalsList;
